import { getAuth } from 'firebase/auth';
import axios from 'axios';
import { FIREBASE_FUNCTION_URL } from '../app-settings';
import { EditProductRequest, ProductFunctionRequest, PublishedAndDraft } from '../models/CloudFunction';
import { SearchParams, SearchResponse } from 'typesense/lib/Typesense/Documents';
import { typesenseClient } from '../typesense';
import { DbProduct, DbProductOverview } from '../models/Product';

const OVERVIEW_FIELDS = ["id", "productId", "name", "released", "imageUrl", "detailsUrl", "productType", "order", "generallyAvailable"];

export async function getNewProductId(): Promise<number> {
    try {
        const idLookup = await typesenseClient
            .collections<DbProduct>("products")
            .documents()
            .search({
                q: "*",
                sort_by: "productId:desc",
                limit: 1
            });

        if (!idLookup.hits?.length) {
            const errorMessage = "Could not compute a new product ID.";
            console.log(errorMessage);
            throw Error();
        }

        const newId = idLookup.hits![0].document.productId + 1;
        return newId;
    } catch (e) {
        return Promise.reject("Error getting new product ID.");
    }
}

export async function getAllProductOverviews(): Promise<DbProductOverview[]> {
    try {
        const results = await typesenseClient
            .collections<DbProductOverview>("products")
            .documents()
            .export({
                include_fields: OVERVIEW_FIELDS.join(",")
            });

        const lines = results.split("\n");
        const objects = lines.map(x => JSON.parse(x) as DbProductOverview);
        return objects;
    } catch (e) {
        return Promise.reject("Error getting products.");
    }
}

export async function searchProducts(params: SearchParams, pendingApproval: boolean): Promise<SearchResponse<DbProductOverview>> {
    try {
        params.include_fields = OVERVIEW_FIELDS;
        params.sort_by = "order:desc";
        const collection = pendingApproval ? "draft-products" : "products";

        const results = await typesenseClient
            .collections<DbProductOverview>(collection)
            .documents()
            .search(params);

        return results;
    } catch (e) {
        return Promise.reject("Error searching products.");
    }
}

export async function getProduct(id: string): Promise<DbProduct> {
    try {
        const result = await typesenseClient
            .collections<DbProduct>("products")
            .documents(id)
            .retrieve();

        return result;
    } catch (e) {
        console.log(JSON.stringify(e));
        return Promise.reject("Error fetching products.");
    }
}

export async function publishProduct(data: EditProductRequest): Promise<void> {
    const auth = getAuth();
    if (!auth.currentUser) {
        return Promise.reject();
    }

    const userToken = await auth.currentUser.getIdToken();

    const requestBody: ProductFunctionRequest<EditProductRequest> = {
        functionName: "publish",
        body: data
    };

    const response = await axios.post(`${FIREBASE_FUNCTION_URL}/products`, requestBody, { headers: { Authorization: `Bearer ${userToken}` } });
    return response.data;
}

export async function saveDraftProduct(data: EditProductRequest): Promise<{ id: string }> {
    const auth = getAuth();
    if (!auth.currentUser) {
        return Promise.reject();
    }

    const userToken = await auth.currentUser.getIdToken();

    const requestBody: ProductFunctionRequest<EditProductRequest> = {
        functionName: "savedraft",
        body: data
    };

    const response = await axios.post<{ id: string }>(`${FIREBASE_FUNCTION_URL}/products`, requestBody, { headers: { Authorization: `Bearer ${userToken}` } });
    return response.data;
}

export async function declineProductDraft(id: string | undefined): Promise<void> {
    if (!id) {
        return Promise.reject();
    }

    const auth = getAuth();
    if (!auth.currentUser) {
        return Promise.reject();
    }

    const userToken = await auth.currentUser.getIdToken();

    const requestBody: ProductFunctionRequest<{ id: string }> = {
        functionName: "declinedraft",
        body: { id: id }
    };

    await axios.post(`${FIREBASE_FUNCTION_URL}/products`, requestBody, { headers: { Authorization: `Bearer ${userToken}` } });
}

export async function getPublishedAndDraftProduct(id: string): Promise<PublishedAndDraft<DbProduct>> {
    try {
        const result: PublishedAndDraft<DbProduct> = {
            draft: null,
            published: null
        };

        try {
            const publishedResult = await typesenseClient
                .collections<DbProduct>("products")
                .documents(id)
                .retrieve();

            result.published = publishedResult;
        } catch (e) {
            console.log(e);
        }

        try {
            const publishedResult = await typesenseClient
                .collections<DbProduct>("draft-products")
                .documents(id)
                .retrieve();

            result.draft = publishedResult;
        } catch (e) {
            console.log(e);
        }

        return result;
    } catch (e) {
        return Promise.reject("Error fetching products.");
    }
}